import type { Column, Table } from "@tanstack/react-table";
import { Download, Rows2, Rows3, Search, SlidersHorizontal } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/shared/ui/button/Button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu/DropdownMenu";
import { Input } from "@/shared/ui/input/Input";

import { columnLabel, toCsvCell } from "../columnUtils";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  /** Left-aligned slot — the screen's own search box / filters / bulk actions. */
  toolbar?: ReactNode;
  enableGlobalFilter: boolean;
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  searchPlaceholder: string;
  enableDensityToggle: boolean;
  dense: boolean;
  onToggleDense: () => void;
  exportFileName?: string;
  enableColumnVisibility: boolean;
  hideableColumns: Column<TData, unknown>[];
}

/**
 * The advanced-operations control strip above the grid: global search,
 * the parent's `toolbar` slot, density toggle, CSV export and the column
 * show/hide menu. Rendered by DataTable only when at least one feature is on.
 */
export function DataTableToolbar<TData>({
  table,
  toolbar,
  enableGlobalFilter,
  globalFilter,
  onGlobalFilterChange,
  searchPlaceholder,
  enableDensityToggle,
  dense,
  onToggleDense,
  exportFileName,
  enableColumnVisibility,
  hideableColumns,
}: DataTableToolbarProps<TData>) {
  const handleExport = () => {
    if (!exportFileName) return;
    const cols = table.getVisibleLeafColumns().filter((c) => c.accessorFn != null);
    const header = cols.map((c) => toCsvCell(columnLabel(c)));
    const body = table
      .getFilteredRowModel()
      .rows.map((row) => cols.map((c) => toCsvCell(row.getValue(c.id))).join(","));
    const csv = [header.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${exportFileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {enableGlobalFilter ? (
          <div className="relative max-w-xs flex-1">
            <Search
              className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={globalFilter}
              onChange={(e) => onGlobalFilterChange(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="pl-9"
            />
          </div>
        ) : null}
        {toolbar}
      </div>

      <div className="flex items-center gap-2">
        {enableDensityToggle ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onToggleDense}
            title={dense ? "Comfortable rows" : "Compact rows"}
            aria-label={dense ? "Comfortable rows" : "Compact rows"}
          >
            {dense ? <Rows3 className="h-4 w-4" /> : <Rows2 className="h-4 w-4" />}
          </Button>
        ) : null}

        {exportFileName ? (
          <Button type="button" variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Export
          </Button>
        ) : null}

        {enableColumnVisibility && hideableColumns.length ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-80 w-48 overflow-y-auto">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hideableColumns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  className="capitalize"
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
                  onSelect={(e) => e.preventDefault()}
                >
                  {columnLabel(column)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
